import React from 'react';
import { Award, Plane, Clock, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface JobExperienceIndicatorProps {
  experienceYears?: number;
  flightHours?: number;
  variant?: 'card' | 'detail' | 'compact';
  showLabels?: boolean;
  className?: string;
}

// Experience level thresholds
const EXPERIENCE_LEVELS = [
  { min: 0, max: 2, label: 'Entry', color: 'bg-slate-400' },
  { min: 2, max: 5, label: 'Junior', color: 'bg-blue-500' },
  { min: 5, max: 10, label: 'Mid-Level', color: 'bg-emerald-500' },
  { min: 10, max: 15, label: 'Senior', color: 'bg-violet-500' },
  { min: 15, max: Infinity, label: 'Expert', color: 'bg-amber-500' },
];

// Flight hours thresholds (aviation specific)
const FLIGHT_HOURS_LEVELS = [
  { min: 0, max: 500, label: 'Entry', color: 'bg-slate-400' },
  { min: 500, max: 1500, label: 'Low Time', color: 'bg-blue-500' },
  { min: 1500, max: 3000, label: 'Mid Time', color: 'bg-emerald-500' },
  { min: 3000, max: 6000, label: 'High Time', color: 'bg-violet-500' },
  { min: 6000, max: Infinity, label: 'ATP Level', color: 'bg-amber-500' },
];

const getExperienceLevel = (years: number) => {
  return EXPERIENCE_LEVELS.find(l => years >= l.min && years < l.max) || EXPERIENCE_LEVELS[4];
};

const getFlightHoursLevel = (hours: number) => {
  return FLIGHT_HOURS_LEVELS.find(l => hours >= l.min && hours < l.max) || FLIGHT_HOURS_LEVELS[4];
};

const calculateProgress = (value: number, max: number): number => {
  return Math.min((value / max) * 100, 100);
};

const JobExperienceIndicator: React.FC<JobExperienceIndicatorProps> = ({
  experienceYears,
  flightHours,
  variant = 'card',
  showLabels = true,
  className,
}) => {
  if (!experienceYears && !flightHours) return null;

  const expLevel = experienceYears ? getExperienceLevel(experienceYears) : null;
  const hoursLevel = flightHours ? getFlightHoursLevel(flightHours) : null;

  // Compact variant - single line badges
  if (variant === 'compact') {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {experienceYears && expLevel && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/60">
            <Award className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs font-medium">{experienceYears}+ yrs</span>
            <span className={cn(
              "text-[10px] px-1.5 py-0.5 rounded font-medium text-white",
              expLevel.color
            )}>
              {expLevel.label}
            </span>
          </div>
        )}
        {flightHours && hoursLevel && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/60">
            <Plane className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs font-medium">{flightHours.toLocaleString()} hrs</span>
            <span className={cn(
              "text-[10px] px-1.5 py-0.5 rounded font-medium text-white",
              hoursLevel.color
            )}>
              {hoursLevel.label}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Card variant - progress bars
  return (
    <div className={cn("space-y-3", className)}>
      {/* Experience Years */}
      {experienceYears !== undefined && expLevel && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Award className="w-3.5 h-3.5" />
              {showLabels && <span>Experience</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">{experienceYears}+ years</span>
              <span className={cn(
                "text-[10px] px-1.5 py-0.5 rounded font-medium text-white",
                expLevel.color
              )}>
                {expLevel.label}
              </span>
            </div>
          </div>
          <div className="relative h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={cn(
                "absolute inset-y-0 left-0 rounded-full transition-all duration-500",
                expLevel.color
              )}
              style={{ width: `${calculateProgress(experienceYears, 20)}%` }}
            />
            {/* Gradient overlay for premium look */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        </div>
      )}

      {/* Flight Hours */}
      {flightHours !== undefined && hoursLevel && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Plane className="w-3.5 h-3.5" />
              {showLabels && <span>Flight Hours</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">{flightHours.toLocaleString()} hrs</span>
              <span className={cn(
                "text-[10px] px-1.5 py-0.5 rounded font-medium text-white",
                hoursLevel.color
              )}>
                {hoursLevel.label}
              </span>
            </div>
          </div>
          <div className="relative h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={cn(
                "absolute inset-y-0 left-0 rounded-full transition-all duration-500",
                hoursLevel.color
              )}
              style={{ width: `${calculateProgress(flightHours, 10000)}%` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobExperienceIndicator;
